import React, { useState } from "react";
import { useAuth } from "../lib/auth-context";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { DottedGlowBackground } from "../components/ui/dotted-glow-background";
import Turnstile from "../components/Turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function AuthPage() {
    const [mode, setMode] = useState("login"); // "login" | "register"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaResetKey, setCaptchaResetKey] = useState(0);
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const isLogin = mode === "login";

    const resetCaptcha = () => {
        setCaptchaToken("");
        setCaptchaResetKey((k) => k + 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Preencha e-mail e senha.");
            return;
        }
        if (!isLogin && !name.trim()) {
            toast.error("Informe seu nome.");
            return;
        }
        if (password.length < 6) {
            toast.error("A senha precisa ter pelo menos 6 caracteres.");
            return;
        }
        if (TURNSTILE_SITE_KEY && !captchaToken) {
            toast.error("Confirme o captcha antes de continuar.");
            return;
        }
        setSubmitting(true);
        try {
            if (isLogin) {
                await login({ email, password, cf_turnstile_token: captchaToken });
            } else {
                await register({ email, password, name: name.trim(), cf_turnstile_token: captchaToken });
            }
            navigate("/chat");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Algo deu errado. Tente de novo.";
            toast.error(detail);
            // Token Turnstile é one-shot; reseta após falha para o usuário tentar de novo.
            resetCaptcha();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="grid min-h-[100dvh] w-full grid-cols-1 lg:grid-cols-2 bg-[#0a0a0a] overflow-hidden"
            data-testid="auth-page"
        >
            {/* =================== LADO ESQUERDO (BRANDING MINIMAL) =================== */}
            <aside className="relative hidden lg:block overflow-hidden bg-[#070708]">
                {/* Pontos brilhantes em grade (canvas leve, pausa fora da view) */}
                <DottedGlowBackground
                    className="pointer-events-none"
                    gap={16}
                    radius={1.6}
                    opacity={1}
                    color="rgba(120, 120, 120, 0.55)"
                    glowColor="rgba(34, 197, 94, 0.95)"
                    speedMin={0.3}
                    speedMax={1.6}
                    speedScale={1}
                    backgroundOpacity={0}
                />
                {/* Máscara radial para escurecer cantos e destacar o centro */}
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, transparent 30%, rgba(7,7,8,0.85) 80%, rgba(7,7,8,1) 100%)",
                    }}
                />
                {/* Glow verde centrado */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,197,94,0.18),transparent_60%)]" />
                {/* Conteúdo central */}
                <div className="relative z-10 flex h-full flex-row items-center justify-center px-6 text-white">
                    <p
                        className="font-display text-7xl font-light tracking-tight"
                        style={{ textShadow: "0 4px 24px rgba(0,0,0,0.8)" }}
                    >
                        Linkia
                    </p>
                </div>
            </aside>

            {/* =================== LADO DIREITO (FORMULÁRIO) =================== */}
            <main className="relative flex items-center justify-center bg-[#0a0a0a] px-6 py-10 md:px-12">
                <div className="w-full max-w-md">
                    {/* Title */}
                    <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-neutral-100">
                        {isLogin ? "Entrar" : "Criar conta"}
                    </h2>
                    <p className="mt-2 text-center text-sm text-neutral-400">
                        {isLogin
                            ? "Bem-vindo de volta! Faça login para continuar."
                            : "Crie sua conta e ganhe 500 tokens grátis."}
                    </p>

                    {/* Form */}
                    <form
                        onSubmit={handleSubmit}
                        className="mt-8 flex flex-col gap-y-5"
                        data-testid="auth-form"
                    >
                        {!isLogin && (
                            <div className="space-y-2">
                                <label
                                    htmlFor="name"
                                    className="text-sm font-medium text-neutral-300"
                                >
                                    Nome completo
                                </label>
                                <input
                                    id="name"
                                    data-testid="input-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Como podemos te chamar?"
                                    required
                                    className="mt-2 h-11 w-full rounded-lg border border-transparent bg-neutral-900 px-3 text-sm text-neutral-100 shadow-sm ring-1 ring-white/10 transition outline-none placeholder:text-neutral-500 focus:ring-2 focus:ring-primary/50"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <label
                                htmlFor="email"
                                className="text-sm font-medium text-neutral-300"
                            >
                                E-mail
                            </label>
                            <input
                                id="email"
                                data-testid="input-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="seu-email@exemplo.com"
                                required
                                className="mt-2 h-11 w-full rounded-lg border border-transparent bg-neutral-900 px-3 text-sm text-neutral-100 shadow-sm ring-1 ring-white/10 transition outline-none placeholder:text-neutral-500 focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div className="space-y-2">
                            <label
                                htmlFor="password"
                                className="text-sm font-medium text-neutral-300"
                            >
                                Senha
                            </label>
                            <div className="relative mt-2">
                                <input
                                    id="password"
                                    data-testid="input-password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    required
                                    className="h-11 w-full rounded-lg border border-transparent bg-neutral-900 px-3 pr-11 text-sm text-neutral-100 shadow-sm ring-1 ring-white/10 transition outline-none placeholder:text-neutral-500 focus:ring-2 focus:ring-primary/50"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    data-testid="toggle-password"
                                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-neutral-200 transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {isLogin && (
                                <div className="flex justify-end pt-1">
                                    <button
                                        type="button"
                                        onClick={() => toast.info("Em breve: recuperação de senha.")}
                                        className="text-xs text-neutral-400 hover:text-neutral-200 transition-colors"
                                    >
                                        Esqueci a senha
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="mt-1" data-testid="turnstile-wrapper">
                            <Turnstile
                                siteKey={TURNSTILE_SITE_KEY}
                                theme="dark"
                                resetKey={captchaResetKey}
                                onVerify={(t) => setCaptchaToken(t)}
                                onExpire={() => setCaptchaToken("")}
                                onError={() => setCaptchaToken("")}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting || (TURNSTILE_SITE_KEY && !captchaToken)}
                            data-testid="auth-submit-button"
                            className="mt-2 h-11 w-full cursor-pointer rounded-lg bg-gradient-to-b from-emerald-500 to-emerald-600 text-sm font-medium text-white shadow-[0_8px_24px_rgba(34,197,94,0.35)] transition duration-150 hover:brightness-110 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {isLogin ? "Entrar" : "Cadastre-se"}
                        </button>
                    </form>

                    {/* Switch login/cadastro no rodapé */}
                    <p className="mt-8 text-center text-sm text-neutral-400">
                        {isLogin ? "Não tem uma conta?" : "Já possui uma conta?"}{" "}
                        <button
                            type="button"
                            data-testid={isLogin ? "tab-register" : "tab-login"}
                            onClick={() => { setMode(isLogin ? "register" : "login"); resetCaptcha(); }}
                            className="font-semibold text-neutral-100 transition hover:text-white"
                        >
                            {isLogin ? "Cadastre-se" : "Entrar"}
                        </button>
                    </p>
                </div>
            </main>
        </div>
    );
}

// =====================================================================
// (Removido) ParticlesCanvas — substituído por DottedGlowBackground.
// =====================================================================
